import React, { useState, useEffect, useCallback, useMemo, useContext } from 'react';
import {
    Container,
    CardContent,
    Typography,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    CircularProgress,
    Alert,
    Box,
    styled,
    IconButton,
    Tooltip,
    TablePagination,
    Paper,
    Tabs,
    Tab,
    MenuItem,
    InputLabel, 
    Select, 
    FormControl
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import VisibilityIcon from '@mui/icons-material/Visibility';
import Lottie from 'lottie-react';
import listaCarga from './lista_carga.json';
// Ajusta esta ruta al lugar real donde exportas ContextSession
import { ContextSession } from '../../../services/ContextSession';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import ReasignarProcuradorTgu from './ReasignarProcuradorTgu';

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(odd)': {
        backgroundColor: theme.palette.action.hover,
    },
    '&:last-child td, &:last-child th': {
        border: 0,
    },
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    '&.MuiTableCell-head': {
        backgroundColor: '#19438C',
        color: 'white',
        fontSize: 16,
    },
    '&.MuiTableCell-body': {
        fontSize: 14,
        borderRight: 'none',
        borderLeft: 'none',
    },
}));

const ListaCargaJefe = ({ sucursalId = 1 }) => {
    const [tramites, setTramites] = useState([]);
    const [procuradores, setProcuradores] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const navigate = useNavigate();
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const [tabIndex, setTabIndex] = useState(0);
    const [tipoFiltro, setTipoFiltro] = useState('');
    const [tiposTramite, setTiposTramite] = useState([]);
    // Sesión
    const sessi = useContext(ContextSession);

     // Estados para el modal de reasignación
    const [modalAbierto, setModalAbierto] = useState(false);
    const [tramiteSeleccionado, setTramiteSeleccionado] = useState(null);

    const formatFecha = useCallback((fechaString) => {
        if (!fechaString) return 'No asignada';
        const options = { year: 'numeric', month: 'long', day: 'numeric' };
        return new Date(fechaString).toLocaleDateString('es-HN', options);
    }, []);

    // Función para cargar datos
    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            
            // Obtener tipos de trámite
            const tiposResponse = await fetch(
                `https://bei360.injupemp.gob.hn:8444/api/tramites-administrativos/tipos/?sucursal_id=1`
            );
            if (!tiposResponse.ok) throw new Error('Error al cargar los tipos de trámite');
            const tiposData = await tiposResponse.json();
            setTiposTramite(tiposData);
            
            // Obtener trámites
            const tramitesResponse = await fetch(
                `https://bei360.injupemp.gob.hn:8444/api/tramites-administrativos/por-sucursal/?sucursal_id=1`
            );
            if (!tramitesResponse.ok) throw new Error('Error al cargar los trámites');
            const tramitesData = await tramitesResponse.json();
            setTramites(tramitesData);
            
            // Obtener procuradores
            const procuradoresResponse = await fetch(
                'https://bei360.injupemp.gob.hn:8444/api/auth/oficialesLegal/'
            );
            if (!procuradoresResponse.ok) throw new Error('Error al cargar los procuradores');
            const procuradoresData = await procuradoresResponse.json();
            setProcuradores(procuradoresData);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [sucursalId]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleTipoFiltroChange = useCallback((event) => {
    const val = event.target.value;
    setTipoFiltro(val === '' ? '' : Number(val)); // fuerza number o vacío
    setPage(0);
    }, []);

    const filteredTramites = useMemo(() => {
    let result = tramites;

    // Abierto/Cerrado
    result = tabIndex === 0
        ? result.filter(t => t.estado === 'Abierto')
        : result.filter(t => t.estado === 'Cerrado');

    // Tipo
    if (tipoFiltro !== '') {
        result = result.filter(t => Number(t.tipo) === Number(tipoFiltro));
    }

    return result;
    }, [tramites, tabIndex, tipoFiltro]);

 
    const handleChangePage = useCallback((event, newPage) => {
        setPage(newPage);
    }, []);
 
    const handleChangeRowsPerPage = useCallback((event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    }, []);

    const handleVerDetalles = useCallback((tramiteId) => {
        sessi.setVariable("tramiteID", tramiteId);
        // Navegar a la ruta anidada bajo Legal
        navigate(`/Legal/detalles/${tramiteId}`);
    }, [navigate, sessi]);
  // Abrir modal para reasignar procurador
    const abrirModalReasignacion = useCallback((tramite) => {
        setTramiteSeleccionado(tramite);
        setModalAbierto(true);
    }, []);

    // Cerrar modal
    const cerrarModal = useCallback(() => {
        setModalAbierto(false);
        setTramiteSeleccionado(null);
    }, []);

    // Función para actualizar trámite después de reasignación - ACTUALIZADA
    const actualizarTramiteReasignado = useCallback(async (resultadoReasignacion) => {
        const { tramite_id, nuevo_procurador_id, nuevo_procurador_nombre } = resultadoReasignacion;
        
        // Actualización optimista en el estado local
        setTramites(prevTramites => 
            prevTramites.map(tramite => 
                tramite.id === tramite_id 
                    ? { 
                        ...tramite, 
                        procurador_id: nuevo_procurador_id,
                        procurador: nuevo_procurador_nombre
                    } 
                    : tramite
            )
        );
        
        try {
            await fetchData();
        } catch (error) {
            console.error('Error al recargar datos después de reasignación:', error);
            // Mostrar mensaje de error al usuario si es necesario
        }
    }, [fetchData]);


    // Calcular los trámites para la página actual
    const paginatedTramites = filteredTramites.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
    );
 
    const handleTabChange = useCallback((event, newValue) => {
        setTabIndex(newValue);
        setPage(0);
    }, []);

    return (
        <Container maxWidth={false} sx={{ mt: 4, px: { xs: 2, md: 4 } }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography variant="h4" fontWeight="bold">Listado de Cargas</Typography>
            </div>

         <CardContent>
            <Box sx={{
                    
                    width: { xs: '100%', sm: '96vw', md: '92vw', lg: '88vw', xl: '82vw' },
                    maxWidth: 1600,           
                    mx: 'auto'               
                    }}>
                {error && <Alert severity="error" sx={{ mb: 2 }}>Error: {error}</Alert>}

                {loading ? (
                <Box display="flex" flexDirection="column" alignItems="center" mt={3}>
                    <CircularProgress color="primary" />
                    <Typography variant="body2" sx={{ mt: 4}}>Cargando trámites...</Typography>
                </Box>
                ) : (
                <>
               
               
                   <Box
                           sx={{
                             display: 'flex',
                             justifyContent: 'center',
                             margin: '0,5px 0',
                             height: '120px' // 
                           }}
                         >
                        <Lottie
                        animationData={listaCarga}
                        loop
                        autoplay
                        style={{ width: 250, height: 190 }}  
                        />
                    </Box>

                    <Box
                        sx={{
                            position: 'sticky',
                            top: 0,
                            zIndex: 1,
                            mb: 1,
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', md: '240px 1fr 240px' },
                            alignItems: 'center',
                            gap: 2,
                        }}
                        >
                        {/* Izquierda: filtro */}
                        <FormControl
                            size="small"
                            sx={{ minWidth: 240, gridColumn: { xs: '1 / -1', md: '1' }, justifySelf: 'start' }}
                        >
                            <InputLabel id="tipo-tramite-filter-label">Filtrar por tipo</InputLabel>
                            <Select
                            labelId="tipo-tramite-filter-label"
                            id="tipo-tramite-filter"
                            value={tipoFiltro}
                            label="Filtrar por tipo"
                            onChange={handleTipoFiltroChange}
                            >
                            <MenuItem value=""><em>Todos los tipos</em></MenuItem>
                            {tiposTramite.map((tipo) => (
                                <MenuItem key={tipo.id} value={tipo.id}>{tipo.nombre}</MenuItem>
                            ))}
                            </Select>
                        </FormControl>
                <Box sx={{ position: 'sticky', top: 0, zIndex: 1, display: 'flex', justifyContent: 'center', my: 5 }}>
                    <Paper square>
                        <Tabs
                            value={tabIndex}
                            onChange={handleTabChange}
                            sx={{
                            gridColumn: { xs: '1 / -1', md: '2' },
                            justifySelf: 'center',
                            '& .MuiTabs-indicator': { backgroundColor: '#19438C', height: 3, borderRadius: 3 },
                            }}
                        >
                            <Tab
                            label="Activos"
                            sx={{
                                color: tabIndex === 0 ? '#19438C' : '#000001',
                                fontWeight: 'medium',
                                '&.Mui-selected': {
                                color: '#19438C',
                                },
                            }}
                            />
                            <Tab
                            label="Cerrados"
                            sx={{
                                color: tabIndex === 0 ? '#19438C' : '#000001',
                                fontWeight: 'medium',
                                '&.Mui-selected': {
                                color: '#19438C',
                                },
                            }}
                            />
                        </Tabs>
                     </Paper>
                </Box>
                      
                        <Box sx={{ display: { xs: 'none', md: 'block' }, width: 240 }} />
                        </Box>

                    {/* Tabla con scroll horizontal si se desborda */}
                        <Box sx={{ overflowX: 'auto', width: 1 }}>
                        <Table>
                            <TableHead>
                            <StyledTableRow>
                                <StyledTableCell>ID</StyledTableCell>
                                <StyledTableCell>Fecha Creación</StyledTableCell>
                                <StyledTableCell>Procurador</StyledTableCell>
                                <StyledTableCell>Nombre del participante</StyledTableCell>
                                <StyledTableCell>Fecha de Asignación</StyledTableCell>
                                {tabIndex === 1 && <StyledTableCell>Fecha de Cierre</StyledTableCell>}
                                <StyledTableCell>Estado</StyledTableCell>
                                <StyledTableCell>Acciones</StyledTableCell>
                            </StyledTableRow>
                            </TableHead>
                            <TableBody>
                            {paginatedTramites.length > 0 ? (
                                paginatedTramites.map((tramite) => (
                                <StyledTableRow key={tramite.id}>
                                    <StyledTableCell>{tramite.id}</StyledTableCell>
                                    <StyledTableCell>{formatFecha(tramite.fecha_creacion)}</StyledTableCell>
                                    <StyledTableCell>{tramite.procurador || 'N/A'}</StyledTableCell>
                                    <StyledTableCell>{tramite.nombre_del_participante || 'N/A'}</StyledTableCell>
                                    <StyledTableCell>{formatFecha(tramite.fecha_asignacion)}</StyledTableCell>
                                    {tabIndex === 1 && <StyledTableCell>
                                                {tramite.estado.toLowerCase() === 'cerrado' && tramite.fecha_cierre 
                                                    ? formatFecha(tramite.fecha_cierre) 
                                                    : 'N/A'
                                                }
                                            </StyledTableCell>}
                                    <StyledTableCell>
                                    <Box
                                        component="span"
                                        sx={{
                                        color: tramite.estado === 'Abierto' ? '#4CAF50' : '#f80d0dff',
                                        fontWeight: 'bold'
                                        }}
                                    >
                                        {tramite.estado}
                                    </Box>
                                    </StyledTableCell>
                                     
                                    <StyledTableCell>
                                        <Box display="flex" justifyContent="center" alignItems="center">
                                            <Tooltip title="Ver detalles" arrow>
                                                <IconButton
                                                    variant="outlined"
                                                    size="small"
                                                    sx={{ color: '#19438C', mr: 1 }}
                                                    onClick={() => handleVerDetalles(tramite.id)}
                                                >
                                                    <VisibilityIcon />
                                                </IconButton>
                                            </Tooltip>
                                            {tramite.estado === 'Abierto' && (
                                                <Tooltip title="Reasignar procurador" arrow>
                                                    <IconButton
                                                    onClick={() => abrirModalReasignacion(tramite)}
                                                    sx={{
                                                        color: '#19438C',
                                                        backgroundColor: 'rgba(107, 197, 208, 0.1)',
                                                        '&:hover': {
                                                        backgroundColor: 'rgba(107, 197, 208, 0.2)',
                                                        }
                                                    }}
                                                    >
                                                    <GroupAddIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </Box>
                                    </StyledTableCell>

                                </StyledTableRow>
                                ))
                            ) : (
                                <TableRow>
                                {/* Ajustado a 8 columnas */}
                                <TableCell colSpan={8} align="center">
                                    No hay trámites {tabIndex === 0 ? 'abiertos' : 'cerrados'} para esta sucursal
                                </TableCell>
                                </TableRow>
                            )}
                            </TableBody>
                        </Table>
                        </Box>

                        {/* Paginación centrada */}
                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                        <TablePagination
                            rowsPerPageOptions={[5, 10, 20]}
                            component="div"
                            count={filteredTramites.length}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            labelRowsPerPage="Filas por página"
                            labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
                        />
                        </Box>
                    </>
                    )}
                </Box>
                </CardContent>
           {/* Modal para reasignar procurador */}
            <ReasignarProcuradorTgu
                open={modalAbierto}
                onClose={cerrarModal}
                tramite={tramiteSeleccionado}
                procuradores={procuradores}
                onReasignacionExitosa={actualizarTramiteReasignado}
            />
        </Container>
    );
};

export default ListaCargaJefe;